import { Tag, Typography } from '@navi/web-ui/lib/primitives';
import { NaviTableColumnDef } from '@cp/src/components/NaviTable/interfaces';
import dayjs from 'dayjs';
import { DateFormat } from '@cp/src/utils/DateHelper';
import { AnomalyPriorityColor, IAnomalyData } from '../../types';
import Actions from '../../components/TableCells/Actions';
import RaisedFor from '../../components/TableCells/RaisedFor';

export const getClosedAnomalyColumnDefs = (
  viewAnomalyHandler: (anomalyData: IAnomalyData) => void
): NaviTableColumnDef<any, any>[] => {
  return [
    {
      accessorKey: 'anomalyId',
      header: 'Anomaly ID',
      size: 110
    },
    {
      accessorKey: 'createdAt',
      header: 'Created Date',
      size: 120,
      enableSorting: true,
      cell: info => {
        const value = info.row.original?.createdAt;
        return dayjs(value)?.format(DateFormat.LONG_DATE_FORMAT_WITHOUT_TIME);
      }
    },
    {
      accessorKey: 'subtype',
      header: 'Type',
      enableSorting: true,
      size: 200,
      cell: info => {
        const { type, subtype } = info.row.original || {};
        return (
          <div className="w-full">
            <Typography color="var(--grayscale-2)" variant="h4" className="truncate">
              {subtype}
            </Typography>
            <Typography color="var(--grayscale-3)" className="truncate mt-1" variant="h4">
              ({type})
            </Typography>
          </div>
        );
      },
      tooltip: info => {
        const { type, subtype } = info.row.original || {};
        return {
          content: (
            <div>
              <Typography variant="p4">Anomaly Type: {subtype}</Typography>
              <Typography className="mt-1" variant="p4">
                Anomaly Description: {type}
              </Typography>
            </div>
          )
        };
      }
    },
    {
      accessorKey: 'raisedFor',
      header: 'Raised For',
      size: 160,
      cell: info => <RaisedFor info={info?.row?.original} />
    },
    {
      accessorKey: 'agency',
      header: 'Agency',
      size: 140,
      tooltip: true,
      cell: info => {
        const { raisedFor } = info.row.original || {};
        return (
          <Typography color="var(--grayscale-2)" variant="h4" className="truncate">
            {raisedFor?.agencyName}
          </Typography>
        );
      }
    },
    {
      accessorKey: 'priority',
      header: 'Priority',
      enableSorting: true,
      size: 110,
      cell: info => {
        const priority: string = info.row.original.priority;
        const color = AnomalyPriorityColor[priority as keyof typeof AnomalyPriorityColor];
        if (!priority || !color) return null;
        return <Tag color={color} variant="transparent" label={priority} />;
      }
    },
    {
      accessorKey: 'resolutionAt',
      header: 'Resolved Date',
      enableSorting: true,
      size: 140,
      cell: info => {
        const value = info.row.original?.resolvedAt;
        return dayjs(value)?.format(DateFormat.LONG_DATE_FORMAT_WITHOUT_TIME);
      }
    },
    {
      accessorKey: 'resolutionType',
      header: 'Resolution Type',
      size: 140,
      enableSorting: true,
      cell: info => {
        const { resolution } = info.row.original || {};
        return (
          <Typography color="var(--grayscale-2)" variant="h4" className="truncate">
            {resolution?.type}
          </Typography>
        );
      }
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      size: 150,
      cell: info => <Actions viewAnomalyHandler={viewAnomalyHandler} info={info?.row?.original} />
    }
  ];
};
